import { useEffect, useRef } from "react";
import { useTranslate } from "ra-core";
import { Card, CardContent } from "@/components/ui/card";

import { useMediaUrl } from "../echoflicks/useMediaUrl";
import { ExperimentVideo } from "./ExperimentVideo";
import { imageKey } from "./types";
import type {
  ExperimentArm,
  ExperimentManifest,
  ExperimentPhoto,
  MediaRef,
  VideoRef,
} from "./types";

type Clip = { arm: ExperimentArm; media: VideoRef; poster?: MediaRef };

/**
 * Every item of an entry on one page, one card each, with its own players: for entries judged by
 * ear (voices, accents), where the item is what the reviewer compares. Starting a clip pauses
 * every other one, so only one is ever audible.
 */
export const ExperimentGallery = ({
  manifest,
  arms,
}: {
  manifest: ExperimentManifest;
  arms: ExperimentArm[];
}) => {
  const translate = useTranslate();
  const container = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = container.current;
    if (!node) return;
    // Media "play" events do not bubble, so listen in the capture phase.
    const onPlay = (event: Event) => {
      node
        .querySelectorAll<HTMLMediaElement>("audio, video")
        .forEach((player) => {
          if (player !== event.target) player.pause();
        });
    };
    node.addEventListener("play", onPlay, true);
    return () => node.removeEventListener("play", onPlay, true);
  }, []);

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        {translate("crm.lab_results.gallery_help")}
      </p>
      <div ref={container} className="grid gap-4 xl:grid-cols-2">
        {manifest.photos.map((item) => (
          <GalleryCard
            key={item.id}
            item={item}
            clips={clipsOf(manifest, arms, item)}
          />
        ))}
      </div>
    </div>
  );
};

const clipsOf = (
  manifest: ExperimentManifest,
  arms: ExperimentArm[],
  item: ExperimentPhoto,
): Clip[] =>
  arms.flatMap((arm) => {
    const key = imageKey(item.id, arm.id);
    const media = manifest.videos[key];
    return media ? [{ arm, media, poster: manifest.images[key] }] : [];
  });

const GalleryCard = ({
  item,
  clips,
}: {
  item: ExperimentPhoto;
  clips: Clip[];
}) => {
  const title = item.label ?? item.id;
  const films = clips.filter((clip) => !clip.media.audio_only);
  const sounds = clips.filter((clip) => clip.media.audio_only);
  return (
    <Card>
      <CardContent className="py-4 space-y-3">
        <div className="space-y-1">
          <h2 className="text-lg font-semibold">{title}</h2>
          {item.caption && (
            <p className="text-sm text-muted-foreground">{item.caption}</p>
          )}
        </div>
        {films.length > 0 && (
          <div
            className={`grid gap-3 ${films.length > 1 ? "sm:grid-cols-2" : ""}`}
          >
            {films.map(({ arm, media, poster }) => (
              <figure key={arm.id} className="space-y-1">
                <figcaption
                  className="text-sm font-medium"
                  title={arm.description}
                >
                  {arm.label}
                </figcaption>
                <ExperimentVideo
                  media={media}
                  poster={poster}
                  label={`${title} – ${arm.label}`}
                  controls
                  className="w-full aspect-video rounded-md bg-black object-contain"
                />
              </figure>
            ))}
          </div>
        )}
        {sounds.map(({ arm, media }) => (
          <div key={arm.id} className="space-y-1">
            <div className="text-sm font-medium" title={arm.description}>
              {arm.label}
            </div>
            <GalleryAudio media={media} label={`${title} – ${arm.label}`} />
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

/** An audio-only sample as a plain audio bar; the title-card picture adds nothing here. */
const GalleryAudio = ({ media, label }: { media: VideoRef; label: string }) => {
  const state = useMediaUrl({
    storage_bucket: media.bucket,
    storage_path: media.key,
  });
  if (state.status === "error") {
    return <p className="text-xs text-destructive">{state.error}</p>;
  }
  return (
    <audio
      src={state.url ?? undefined}
      aria-label={label}
      preload="none"
      controls
      className="w-full"
    />
  );
};
